import {AreaChart} from '@mantine/charts';
import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import type {CloudWatchMetricSeriesResponse} from 'src/api/ProactApiClient';
import {Pill} from 'src/design/atoms/Pill';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {SegmentedControl} from 'src/design/atoms/SegmentedControl';
import {ErrorAlert} from 'src/design/molecules/ErrorAlert';
import {NoItemsPanel} from 'src/design/molecules/NoItemsPanel';
import {
  ALARM_STATE_META,
  METRIC_RANGE_OPTIONS,
  formatChartTick,
  formatMetricIdentity,
  formatThreshold,
} from '../cloudWatchDisplay';

export interface CloudWatchMetricsPanelProps {
  series: CloudWatchMetricSeriesResponse[];
  hours: number;
  onHoursChange: (hours: number) => void;
  isLoading: boolean;
  isRefreshing?: boolean;
  isError: boolean;
  errorMessage?: string;
}

interface SeriesCardProps {
  item: CloudWatchMetricSeriesResponse;
  hours: number;
}

const SeriesCard = ({item, hours}: SeriesCardProps) => {
  const meta = ALARM_STATE_META[item.state];
  const data = item.points.map(point => ({
    tick: formatChartTick(point.timestamp, hours),
    value: point.value,
  }));

  return (
    <SectionCard
      title={item.label}
      rightSection={<Pill text={meta.label} color={meta.color} />}>
      <Box p="md">
        <Group gap="xs" mb="xs">
          <Text size="xs" c="dimmed">
            {formatMetricIdentity(item.namespace, item.metricName)}
          </Text>
          <Text size="xs" c="dimmed">
            ·
          </Text>
          <Text size="xs" c="dimmed">
            {item.statistic ?? '—'}
          </Text>
          <Text size="xs" c="dimmed">
            ·
          </Text>
          <Text size="xs" c="dimmed">
            {formatThreshold(item.comparisonOperator, item.threshold)}
          </Text>
        </Group>
        {data.length === 0 ? (
          <Text size="sm" c="dimmed" py="xl" ta="center">
            No datapoints reported in this range.
          </Text>
        ) : (
          <AreaChart
            h={180}
            data={data}
            dataKey="tick"
            series={[{name: 'value', label: item.metricName, color: 'blue.6'}]}
            curveType="linear"
            withDots={false}
            unit={item.unit}
            referenceLines={
              item.threshold !== undefined
                ? [
                    {
                      y: item.threshold,
                      color: 'red.6',
                      label: `Threshold ${item.threshold}`,
                    },
                  ]
                : undefined
            }
          />
        )}
      </Box>
    </SectionCard>
  );
};

export const CloudWatchMetricsPanel = ({
  series,
  hours,
  onHoursChange,
  isLoading,
  isRefreshing = false,
  isError,
  errorMessage,
}: CloudWatchMetricsPanelProps) => (
  <Stack gap="md">
    <Group justify="space-between">
      <Text size="sm" c="dimmed">
        One chart per metric alarm. A metric with no alarm is not charted.
      </Text>
      <Group gap="xs">
        {isRefreshing && <Loader size="xs" />}
        <SegmentedControl
          value={String(hours)}
          onChange={value => onHoursChange(Number(value))}
          data={METRIC_RANGE_OPTIONS}
          aria-label="Metric range"
        />
      </Group>
    </Group>

    {isError ? (
      <ErrorAlert message={errorMessage ?? 'Failed to load metrics.'} />
    ) : isLoading ? (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    ) : series.length === 0 ? (
      <NoItemsPanel noItemsText="No metric alarms are defined for this environment, so there is nothing to chart." />
    ) : (
      <SimpleGrid cols={{base: 1, md: 2}} spacing="md">
        {series.map(item => (
          <SeriesCard key={item.alarmName} item={item} hours={hours} />
        ))}
      </SimpleGrid>
    )}
  </Stack>
);
